"use client";
import { unOrderedListToArray } from "@/hooks/use-service-content";
import Link from "next/link";
import { useMemo } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "../ui/card";
import { Box } from "../ui/box";
import { Heading } from "../ui/heading";
import { Text } from "../ui/text";
import { CheckCircleIcon } from "lucide-react";
import { Button } from "../ui/button";
import { AspectRatio } from "../ui/aspect-ratio";
import Image from "next/image";

export type ServiceContentItem = {
  heading: string;
  sub_heading: string;
  is_html_description: boolean;
  link: string;
  image: string;
  description: string;
};

export type ServiceContents = {
  description: ServiceContentItem;
  advantages: ServiceContentItem;
  interestingFact: ServiceContentItem;
};

export type ConsultingCardProps = {
  id: number;
  slug: string;
  name: string;
  service_image: string;
  service_contents: ServiceContents;
};

export default function ConsultingCard({
  name,
  service_image,
  slug,
  service_contents,
}: ConsultingCardProps) {
  const advantages = useMemo((): string | string[] => {
    if (service_contents.advantages.is_html_description) {
      const items = unOrderedListToArray(service_contents.advantages.description);
      return items.length > 0 ? items : service_contents.advantages.description;
    }
    return service_contents.advantages.description;
  }, [service_contents.advantages]);

  if (!name) return null;

  return (
    <Card className="group relative flex h-full w-full flex-col overflow-hidden rounded-3xl transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(15,23,42,0.1)]">
      <CardHeader>
        <AspectRatio className="w-full overflow-hidden rounded-2xl" ratio={564 / 220}>
          <span className="absolute inset-0 z-9 bg-linear-to-t from-black/25 to-transparent" />
          <Heading
            as="h3"
            className="absolute right-5 bottom-4 left-5 z-10 text-xl font-bold tracking-tight text-white sm:text-2xl"
          >
            {name}
          </Heading>
          <Image
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            alt={name}
            fill
            sizes="300px"
            src={service_image}
          />
        </AspectRatio>
      </CardHeader>
      {/* Card Content body */}
      <CardContent className="flex grow flex-col gap-4 px-6 pt-3 pb-5 sm:px-7">
        {/* Main description */}
        <Text className="text-sm leading-relaxed">
          {service_contents?.description?.description}
        </Text>

        {/* Advantages checklist */}
        <Box>
          <Text className="text-muted-foreground mb-3 text-base font-bold">Advantages</Text>

          {Array.isArray(advantages) ? (
            <ul className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
              {advantages.map((item, index) => (
                <li
                  key={`advantages-${index}`}
                  className="text-muted-foreground flex items-start gap-2 text-sm"
                >
                  <span className="bg-background mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-emerald-600">
                    <CheckCircleIcon className="h-3.5 w-3.5" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Text className="text-sm leading-relaxed text-slate-600">{advantages}</Text>
          )}
        </Box>

        {/* Interesting Fact wrapper */}
        <Box className="border-primary from-primary/3 dark:from-background dark:to-background rounded-l-md rounded-r-2xl border-l-[3px] bg-linear-to-r to-slate-50 p-4">
          <Text className="text-primary mb-1 text-sm font-bold tracking-wider uppercase">
            Interesting Fact
          </Text>
          <Text className="mb-0 text-xs leading-relaxed sm:text-sm">
            {service_contents?.interestingFact?.description}
          </Text>
        </Box>
      </CardContent>

      {/* Action CTA Button */}
      <CardFooter className="bg-card border-0">
        <Button
          className="group-hover:bg-primary! w-full transition-all duration-300 group-hover:text-white!"
          nativeButton={false}
          render={<Link href={slug} />}
          size="lg"
          variant={"outline"}
        >
          View {name}
        </Button>
      </CardFooter>
    </Card>
  );
}
